import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { dispatchAgentEvent, toggleAgent } from "@/services/agents";

export const AgentPanel = ({ agents, logs, consciousness, token, onAgentRefresh }) => {
  const list = agents?.agents || agents?.items || agents || [];
  const logList = logs || [];
  const influence = consciousness?.subsystem_influence_levels || {};
  const allocator = consciousness?.resource_allocator || {};
  const priorities = consciousness?.current_priorities || [];
  const [dispatchEvent, setDispatchEvent] = useState("energy_threshold_exceeded");
  const [pendingId, setPendingId] = useState(null);
  const [error, setError] = useState("");

  const toggle = async (id) => {
    setPendingId(id);
    setError("");
    try {
      await toggleAgent(id, token);
      onAgentRefresh?.();
    } catch (agentError) {
      setError(agentError.message || "Agent toggle failed");
    } finally {
      setPendingId(null);
    }
  };

  const dispatch = async () => {
    setPendingId("dispatch");
    setError("");
    try {
      await dispatchAgentEvent(dispatchEvent, { source: "agent_dashboard", current_power: 68 }, token);
      onAgentRefresh?.();
    } catch (agentError) {
      setError(agentError.message || "Dispatch failed");
    } finally {
      setPendingId(null);
    }
  };

  return (
    <section className="jarvis-panel" data-testid="agent-panel">
      <div className="jarvis-panel-header">
        <div>
          <p className="jarvis-eyebrow">Visible multi-agent workforce</p>
          <h2 className="jarvis-title" data-testid="agent-panel-title">AI Agent Orchestration</h2>
        </div>
        <div className="jarvis-chip" data-testid="agent-panel-chip">{list.length} agents</div>
      </div>
      {error ? <div className="jarvis-inline-error" data-testid="agent-panel-error">{error}</div> : null}
      <div className="jarvis-agent-dispatch" data-testid="agent-dispatch-panel">
        <Input value={dispatchEvent} onChange={(event) => setDispatchEvent(event.target.value)} data-testid="agent-dispatch-input" />
        <Button onClick={dispatch} disabled={pendingId === "dispatch"} data-testid="agent-dispatch-button">Dispatch</Button>
      </div>
      <div className="jarvis-agent-summary-grid" data-testid="agent-summary-grid">
        <div className="jarvis-detail-box" data-testid="agent-summary-active-count">
          <span>Active agents</span>
          <strong>{list.filter((agent) => agent.status === "active").length}</strong>
        </div>
        <div className="jarvis-detail-box" data-testid="agent-summary-worker-slots">
          <span>Execution slots</span>
          <strong>{allocator.agent_execution_slots ?? "—"}</strong>
        </div>
        <div className="jarvis-detail-box" data-testid="agent-summary-top-priority">
          <span>Top priority</span>
          <strong>{priorities[0]?.domain || "stable"}</strong>
        </div>
      </div>
      <div className="jarvis-list" data-testid="agent-panel-list">
        {list.slice(0, 8).map((agent) => (
          <div className="jarvis-list-row" key={agent.agent_id || agent.id} data-testid={`agent-panel-row-${agent.agent_id || agent.id}`}>
            <div>
              <strong>{agent.name || agent.agent_id}</strong>
              <small>Role: {agent.role} · {agent.status} · Confidence: {agent.confidence ?? 0.92}</small>
              <small data-testid={`agent-panel-last-action-${agent.agent_id || agent.id}`}>Last action: {agent.last_action || "idle"}</small>
            </div>
            <div className="jarvis-agent-actions" data-testid={`agent-panel-actions-${agent.agent_id || agent.id}`}>
              <span data-testid={`agent-panel-influence-${agent.agent_id || agent.id}`}>{Math.round((influence.agent_system || 0.7) * 100)}%</span>
              <Button size="sm" variant={agent.status === "active" ? "destructive" : "default"} onClick={() => toggle(agent.agent_id || agent.id)} disabled={pendingId === (agent.agent_id || agent.id)} data-testid={`agent-toggle-button-${agent.agent_id || agent.id}`}>
                {agent.status === "active" ? "Disable" : "Enable"}
              </Button>
            </div>
          </div>
        ))}
      </div>
      <div className="jarvis-agent-log-list" data-testid="agent-log-list">
        {logList.slice(0, 6).map((log, index) => (
          <div className="jarvis-log-entry" key={`${log.id || index}-${index}`} data-testid={`agent-log-entry-${index}`}>
            {log.agent_id} · {log.event_type} · {log.action_taken} · {log.result_status} · {Math.round((log.confidence || 0) * 100)}%
          </div>
        ))}
      </div>
    </section>
  );
};